import { Component, effect, inject, input, signal } from '@angular/core';
import { ProjectModel } from '../../../../../core/models/project/project.model';
import { ProjectDataService } from '../../state-and-data-handling/project-data.service';
import { TaskDetailGridRow } from '../../../shared/project-detail-types';
import { BudgetCalculationService } from '../../project-edit/manage-project/project-calculation-form/services/budget-calculation.service';
import { TimeBlockWithTask } from '../../../../../shared/data-types/shared-time-block-types';
import { TimeFormatType } from '../../../../../core/pipes/time-format.pipe';
import {
  BudgetOptions,
  ExpenditureOptions,
} from '../../../../../core/constants-repository/project-calculation.constants';
import { TimeBlockDurationCalculationService } from '../../../../../features/calendar/components/time-block/calculation/duration/time-block-duration-calculation.service';
import { ExpenditureCalculationService } from '../../project-edit/manage-project/project-calculation-form/services/expenditure-calculation.service';

@Component({
  selector: 'app-project-task-summary',
  templateUrl: './project-task-summary.component.html',
  standalone: false,
})
export class ProjectTaskSummaryComponent {
  timeBlocksWithTask = input.required<readonly TimeBlockWithTask[]>();
  budgetVisible = input.required<boolean>();
  gridData = signal<TaskDetailGridRow[]>([]);

  // Constants
  public readonly TimeFormatType = TimeFormatType;
  public readonly BudgetOptions = BudgetOptions;
  public readonly ExpenditureRateOptions = ExpenditureOptions;

  // DI
  private readonly expenditureCalculationService = inject(ExpenditureCalculationService);
  private readonly projectDataService = inject(ProjectDataService);
  private readonly budgetCalculationService = inject(BudgetCalculationService);
  private readonly timeBlockDurationCalculationService = inject(
    TimeBlockDurationCalculationService,
  );

  constructor() {
    effect(() => {
      const timeBlockWithTasks = this.timeBlocksWithTask() ?? [];
      this.gridData.set(this.createGridData(timeBlockWithTasks));
    });
  }

  public get project(): ProjectModel {
    return this.projectDataService.getProjectClone();
  }

  private createGridData(timeBlocksWithTask: readonly TimeBlockWithTask[]): TaskDetailGridRow[] {
    const gridData: TaskDetailGridRow[] = [];
    const filterTimeBlocksWithTask = (taskId: number) =>
      timeBlocksWithTask.filter((timeBlockWithTask) => timeBlockWithTask.task.id === taskId);

    this.projectDataService.getTasks(this.project, null, false).forEach((task) => {
      const timeBlocksWithTask = filterTimeBlocksWithTask(task.id);
      gridData.push({
        task,
        spentSeconds: this.calcSpentSeconds(timeBlocksWithTask),
        spentFees: this.expenditureCalculationService.calcFeeSumForTimeBlock(
          this.project,
          timeBlocksWithTask,
        ),
        budget: this.budgetCalculationService.getBudgetForTask(this.project, task.id),
      });
    });
    return gridData;
  }

  private calcSpentSeconds(timeBlocksWithTask: readonly TimeBlockWithTask[]): number {
    const timeBlocks = timeBlocksWithTask.map((timeBlock) => timeBlock.timeBlock);
    return this.timeBlockDurationCalculationService.calculateTimeBlockTimeSum(timeBlocks, true);
  }
}
